import { Copy, Download, Plus } from "lucide-react";
import React, { useState } from 'react';
import Button from './components/button/Button';
import Modal from './components/Modal';
import Checkbox from './components/checkbox/Checkbox';
import ToggleField from './components/toggle-field/ToggleField';
import Input from './components/input/Input';
import Select from './components/input/Select';
import Textarea from './components/input/Textarea';
import SearchInput from './components/input/SearchInput';
import Badge from './components/badge/Badge';
import Dropdown from './components/dropdown/Dropdown';
import Breadcrumb from './components/breadcrumb/Breadcrumb';
import MatchRating from './components/match-rating/MatchRating';
import Table from './components/table/Table';
import Spinner from './components/spinner/Spinner';
import Link from './components/link/Link';
import { Heading1, Heading2, Heading3, Heading4, Heading5, Heading6, LeadingParagraph, NormalParagraph, LabelText, BodyText, SmallText, Quote } from './components/typography/Typography';

import { toast } from 'sonner';

const ComponentShowcase = () => {
    const [modalOpen, setModalOpen] = useState(false);
    const [footerGroupModalOpen, setFooterGroupModalOpen] = useState(false);
    const [checkboxValue, setCheckboxValue] = useState(false);
    const [checkboxValue2, setCheckboxValue2] = useState(true);
    const [toggleFieldOn, setToggleFieldOn] = useState(false);
    const [showcaseSearch, setShowcaseSearch] = useState('');

    // State for sortable table
    const [sortableData, setSortableData] = useState([
        { name: 'Alice Cooper', role: 'Product Manager', status: 'Active' },
        { name: 'Charlie Brown', role: 'UX Designer', status: 'Pending' },
        { name: 'Diana Prince', role: 'DevOps Engineer', status: 'Inactive' },
        { name: 'Eve Wilson', role: 'Frontend Developer', status: 'Active' },
        { name: 'Frank Miller', role: 'Backend Developer', status: 'Pending' }
    ]);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

    // State for paginated table
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(3);
    const [paginatedData] = useState([
        { id: 1, name: 'Alice Johnson', role: 'Product Manager', department: 'Product', status: 'Active' },
        { id: 2, name: 'Bob Smith', role: 'Frontend Developer', department: 'Engineering', status: 'Active' },
        { id: 3, name: 'Carol Davis', role: 'UX Designer', department: 'Design', status: 'Pending' },
        { id: 4, name: 'David Wilson', role: 'Backend Developer', department: 'Engineering', status: 'Active' },
        { id: 5, name: 'Eva Brown', role: 'Marketing Manager', department: 'Marketing', status: 'Active' },
        { id: 6, name: 'Frank Miller', role: 'DevOps Engineer', department: 'Engineering', status: 'Inactive' },
        { id: 7, name: 'Grace Lee', role: 'Data Analyst', department: 'Analytics', status: 'Active' },
        { id: 8, name: 'Henry Taylor', role: 'QA Engineer', department: 'Engineering', status: 'Pending' },
        { id: 9, name: 'Ivy Chen', role: 'Content Writer', department: 'Marketing', status: 'Active' },
        { id: 10, name: 'Jack Anderson', role: 'Product Designer', department: 'Design', status: 'Active' },
        { id: 11, name: 'Kate Martinez', role: 'Sales Manager', department: 'Sales', status: 'Active' },
        { id: 12, name: 'Liam O\'Connor', role: 'Mobile Developer', department: 'Engineering', status: 'Active' },
        { id: 13, name: 'Maya Patel', role: 'HR Specialist', department: 'Human Resources', status: 'Active' },
        { id: 14, name: 'Noah Thompson', role: 'Security Engineer', department: 'IT', status: 'Active' },
        { id: 15, name: 'Olivia Garcia', role: 'Business Analyst', department: 'Analytics', status: 'Pending' },
        { id: 16, name: 'Paul Rodriguez', role: 'Network Admin', department: 'IT', status: 'Active' },
        { id: 17, name: 'Quinn Williams', role: 'Legal Counsel', department: 'Legal', status: 'Active' },
        { id: 18, name: 'Ruby Kim', role: 'Customer Success', department: 'Support', status: 'Active' },
        { id: 19, name: 'Sam Johnson', role: 'Operations Manager', department: 'Operations', status: 'Inactive' },
        { id: 20, name: 'Tina Davis', role: 'Financial Analyst', department: 'Finance', status: 'Active' },
        { id: 21, name: 'Uma Singh', role: 'Research Scientist', department: 'R&D', status: 'Active' },
        { id: 22, name: 'Victor Chen', role: 'Supply Chain Manager', department: 'Operations', status: 'Active' },
        { id: 23, name: 'Wendy Brown', role: 'Brand Manager', department: 'Marketing', status: 'Pending' },
        { id: 24, name: 'Xavier Lopez', role: 'Database Admin', department: 'IT', status: 'Active' },
        { id: 25, name: 'Yara Ahmed', role: 'Compliance Officer', department: 'Legal', status: 'Active' },
        { id: 26, name: 'Zoe Wilson', role: 'Product Owner', department: 'Product', status: 'Active' },
        { id: 27, name: 'Adam Foster', role: 'Scrum Master', department: 'Engineering', status: 'Active' },
        { id: 28, name: 'Bella Torres', role: 'UX Researcher', department: 'Design', status: 'Active' },
        { id: 29, name: 'Caleb Mitchell', role: 'Data Engineer', department: 'Analytics', status: 'Pending' }
    ]);

    // Calculate pagination values
    const totalPages = Math.ceil(paginatedData.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentData = paginatedData.slice(startIndex, endIndex);

    // Handle page change
    const handlePageChange = (page) => {
        setCurrentPage(page);
        toast.info(`Navigated to page ${page}`);
    };

    // Handle items per page change
    const handleItemsPerPageChange = (newItemsPerPage) => {
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1); // Reset to first page when changing items per page
        toast.info(`Changed to ${newItemsPerPage} items per page`);
    };

    // Sorting function
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        } else if (sortConfig.key === key && sortConfig.direction === 'desc') {
            direction = null;
        }

        setSortConfig({ key: direction ? key : null, direction });

        if (direction) {
            const sortedData = [...sortableData].sort((a, b) => {
                if (direction === 'asc') {
                    return a[key].localeCompare(b[key]);
                } else {
                    return b[key].localeCompare(a[key]);
                }
            });
            setSortableData(sortedData);
            toast.info(`${key.charAt(0).toUpperCase() + key.slice(1)} column sorted ${direction === 'asc' ? 'ascending' : 'descending'}!`);
        } else {
            // Reset to original order
            setSortableData([
                { name: 'Alice Cooper', role: 'Product Manager', status: 'Active' },
                { name: 'Charlie Brown', role: 'UX Designer', status: 'Pending' },
                { name: 'Diana Prince', role: 'DevOps Engineer', status: 'Inactive' },
                { name: 'Eve Wilson', role: 'Frontend Developer', status: 'Active' },
                { name: 'Frank Miller', role: 'Backend Developer', status: 'Pending' }
            ]);
            toast.info(`${key.charAt(0).toUpperCase() + key.slice(1)} column sorting removed!`);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 p-8">
            <div className="max-w-7xl mx-auto">
                <Heading1 className="mb-8">UI Component Library</Heading1>

                {/* ========================================
                     TYPOGRAPHY SECTION
                     ======================================== */}
                <section className="mb-12">
                    <Heading2 className="mb-6">Typography</Heading2>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="bg-white p-6 rounded-lg shadow-md border">
                            <Heading4 className="mb-4">Headings</Heading4>
                            <div className="space-y-4">
                                <Heading1>Heading 1 - Display Large</Heading1>
                                <Heading2>Heading 2 - Display Medium</Heading2>
                                <Heading3>Heading 3 - Large</Heading3>
                                <Heading4>Heading 4 - Medium</Heading4>
                                <Heading5>Heading 5 - Small</Heading5>
                                <Heading6>Heading 6 - Tiny</Heading6>
                            </div>
                        </div>

                        <div className="bg-white p-6 rounded-lg shadow-md border">
                            <Heading4 className="mb-4">Body text</Heading4>
                            <div className="space-y-4">
                                <BodyText>
                                    BodyText / text-body (12px, 150% line height) — the one size for all body copy: paragraphs, values, captions, table cells, and inputs.
                                </BodyText>
                                <BodyText weight="medium">
                                    Same body size with medium weight (500).
                                </BodyText>
                                <BodyText weight="bold" emphasis>
                                    Same body size with bold weight — use for emphasis within body copy, not as a substitute for headings.
                                </BodyText>
                                <LabelText>
                                    LabelText (16px) — form labels and inline section titles only. Not for paragraphs.
                                </LabelText>
                                <NormalParagraph>
                                    NormalParagraph / text-body-lg (18px) — display/marketing only. Do not use for app body copy.
                                </NormalParagraph>
                                <LeadingParagraph>
                                    LeadingParagraph / text-body-xl (24px) — display/marketing only. Do not use for app body copy.
                                </LeadingParagraph>
                                <Quote>
                                    "Quote is display text for testimonials or callouts — not standard body copy."
                                </Quote>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ========================================
                     BUTTONS SECTION
                     ======================================== */}
                <section className="mb-12">
                    <Heading2 className="mb-6">Buttons</Heading2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-white p-6 rounded-lg shadow-md border">
                            <Heading4 className="mb-4">Primary Button</Heading4>
                            <div className="flex space-x-2 mb-4">
                                <Button variant="primary" onClick={() => toast.success('Primary button clicked!')}>Primary Button</Button>
                                <Button variant="primary" disabled>Disabled</Button>
                            </div>

                            <div className="space-y-2">
                                <div className="flex items-center space-x-2">
                                    <SmallText className="w-16">Default:</SmallText>
                                    <div className="w-3 h-3 btn-primary rounded"></div>
                                    <SmallText>primary-700 to pink-600</SmallText>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <SmallText className="w-16">Hover:</SmallText>
                                    <div className="w-3 h-3 btn-primary-hover rounded"></div>
                                    <SmallText>primary-600</SmallText>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <SmallText className="w-16">Active:</SmallText>
                                    <div className="w-3 h-3 btn-primary-active rounded"></div>
                                    <SmallText>primary-700</SmallText>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <SmallText className="w-16">Disabled:</SmallText>
                                    <div className="w-3 h-3 btn-primary-disabled rounded"></div>
                                    <SmallText>primary-700 (50% opacity)</SmallText>
                                </div>
                                <div className="mt-2 flex items-center space-x-1">
                                    <LabelText>Text: </LabelText><SmallText>white</SmallText>
                                    <span className="mx-1">|</span>
                                    <LabelText>Border: </LabelText><SmallText>none</SmallText>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white p-6 rounded-lg shadow-md border">
                            <Heading4 className="mb-4">Secondary Button</Heading4>
                            <div className="flex space-x-2 mb-4">
                                <Button variant="secondary" onClick={() => toast.success('Secondary button clicked!')}>Secondary Button</Button>
                                <Button variant="secondary" disabled>Disabled</Button>
                            </div>

                            <div className="space-y-2">
                                <div className="flex items-center space-x-2">
                                    <SmallText className="w-16">Default:</SmallText>
                                    <div className="w-3 h-3 btn-secondary rounded"></div>
                                    <SmallText>white</SmallText>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <SmallText className="w-16">Hover:</SmallText>
                                    <div className="w-3 h-3 btn-secondary-hover rounded"></div>
                                    <SmallText>gray-100</SmallText>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <SmallText className="w-16">Active:</SmallText>
                                    <div className="w-3 h-3 btn-secondary-active rounded"></div>
                                    <SmallText>gray-200</SmallText>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <SmallText className="w-16">Disabled:</SmallText>
                                    <div className="w-3 h-3 btn-secondary-disabled rounded"></div>
                                    <SmallText>white (50% opacity)</SmallText>
                                </div>
                                <div className="mt-2 flex items-center space-x-1">
                                    <LabelText>Text: </LabelText><SmallText>gray-600</SmallText>
                                    <span className="mx-1">|</span>
                                    <LabelText>Border: </LabelText><SmallText>gray-200</SmallText>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white p-6 rounded-lg shadow-md border">
                            <Heading4 className="mb-4">Tertiary Button</Heading4>
                            <div className="flex space-x-2 mb-4">
                                <Button variant="tertiary" onClick={() => toast.success('Tertiary button clicked!')}>Tertiary Button</Button>
                                <Button variant="tertiary" disabled>Disabled</Button>
                            </div>

                            <div className="space-y-2">
                                <div className="flex items-center space-x-2">
                                    <SmallText className="w-16">Default:</SmallText>
                                    <div className="w-3 h-3 btn-tertiary rounded"></div>
                                    <SmallText>transparent</SmallText>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <SmallText className="w-16">Hover:</SmallText>
                                    <div className="w-3 h-3 btn-tertiary-hover rounded"></div>
                                    <SmallText>gray-100</SmallText>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <SmallText className="w-16">Active:</SmallText>
                                    <div className="w-3 h-3 btn-tertiary-active rounded"></div>
                                    <SmallText>gray-200</SmallText>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <SmallText className="w-16">Disabled:</SmallText>
                                    <div className="w-3 h-3 btn-tertiary-disabled rounded"></div>
                                    <SmallText>transparent (50% opacity)</SmallText>
                                </div>
                                <div className="mt-2 flex items-center space-x-1">
                                    <LabelText>Text: </LabelText><SmallText>primary-700</SmallText>
                                    <span className="mx-1">|</span>
                                    <LabelText>Border: </LabelText><SmallText>none</SmallText>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white p-6 rounded-lg shadow-md border">
                            <Heading4 className="mb-4">Primary with Icons</Heading4>
                            <div className="flex space-x-2 mb-4">
                                <Button variant="primary" leftIcon={<Plus size="1em" />} onClick={() => toast.success('Primary Add Item clicked!')}>Add Item</Button>
                                <Button variant="primary" rightIcon={<Download size="1em" />} onClick={() => toast.success('Primary Download clicked!')}>Download</Button>
                                <Button variant="primary" leftIcon={<Copy size="1em" />} rightIcon={<Copy size="1em" />} onClick={() => toast.success('Primary Copy clicked!')}>Copy</Button>
                            </div>
                        </div>

                        <div className="bg-white p-6 rounded-lg shadow-md border">
                            <Heading4 className="mb-4">Secondary with Icons</Heading4>
                            <div className="flex space-x-2 mb-4">
                                <Button variant="secondary" leftIcon={<Plus size="1em" />} onClick={() => toast.success('Secondary Add Item clicked!')}>Add Item</Button>
                                <Button variant="secondary" rightIcon={<Download size="1em" />} onClick={() => toast.success('Secondary Download clicked!')}>Download</Button>
                                <Button variant="secondary" leftIcon={<Copy size="1em" />} rightIcon={<Copy size="1em" />} onClick={() => toast.success('Secondary Copy clicked!')}>Copy</Button>
                            </div>
                        </div>

                        <div className="bg-white p-6 rounded-lg shadow-md border">
                            <Heading4 className="mb-4">Tertiary with Icons</Heading4>
                            <div className="flex space-x-2 mb-4">
                                <Button variant="tertiary" leftIcon={<Plus size="1em" />} onClick={() => toast.success('Tertiary Add Item clicked!')}>Add Item</Button>
                                <Button variant="tertiary" rightIcon={<Download size="1em" />} onClick={() => toast.success('Tertiary Download clicked!')}>Download</Button>
                                <Button variant="tertiary" leftIcon={<Copy size="1em" />} rightIcon={<Copy size="1em" />} onClick={() => toast.success('Tertiary Copy clicked!')}>Copy</Button>
                            </div>
                        </div>

                        <div className="bg-white p-6 rounded-lg shadow-md border">
                            <Heading4 className="mb-4">Danger Button</Heading4>
                            <div className="flex space-x-2 mb-4">
                                <Button variant="danger" onClick={() => toast.success('Danger button clicked!')}>Danger Button</Button>
                                <Button variant="danger" disabled>Disabled</Button>
                            </div>

                            <div className="space-y-2">
                                <div className="flex items-center space-x-2">
                                    <SmallText className="w-16">Default:</SmallText>
                                    <div className="w-3 h-3 btn-danger rounded"></div>
                                    <SmallText>transparent</SmallText>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <SmallText className="w-16">Hover:</SmallText>
                                    <div className="w-3 h-3 btn-danger-hover rounded"></div>
                                    <SmallText>gray-100</SmallText>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <SmallText className="w-16">Active:</SmallText>
                                    <div className="w-3 h-3 btn-danger-active rounded"></div>
                                    <SmallText>gray-200</SmallText>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <SmallText className="w-16">Disabled:</SmallText>
                                    <div className="w-3 h-3 btn-danger-disabled rounded"></div>
                                    <SmallText>transparent (50% opacity)</SmallText>
                                </div>
                                <div className="mt-2 flex items-center space-x-1">
                                    <LabelText>Text: </LabelText><SmallText>red-700</SmallText>
                                    <span className="mx-1">|</span>
                                    <LabelText>Border: </LabelText><SmallText>none</SmallText>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-6 bg-white p-6 rounded-lg shadow-md border">
                        <Heading4 className="mb-2">Dialog footer button group</Heading4>
                        <NormalParagraph className="mb-4 text-gray-600">
                            Modals and multi-step forms: <strong>ghost Cancel</strong> on the left;
                            <strong> secondary</strong> (optional) then <strong>primary Save</strong> on the right.
                            See design-system principle #10.
                        </NormalParagraph>
                        <div className="rounded-lg border border-gray-200 overflow-hidden">
                            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                                <SmallText className="text-gray-500">Example footer strip</SmallText>
                            </div>
                            <div className="px-4 py-3 flex flex-wrap items-center justify-between gap-3">
                                <Button
                                    variant="ghost"
                                    onClick={() => toast.info('Cancel — ends flow without saving')}
                                >
                                    Cancel
                                </Button>
                                <div className="flex flex-wrap items-center justify-end gap-3 ml-auto">
                                    <Button
                                        variant="secondary"
                                        onClick={() => toast.info('Reset to Default')}
                                    >
                                        Reset to Default
                                    </Button>
                                    <Button
                                        variant="primary"
                                        onClick={() => toast.success('Save — primary commit')}
                                    >
                                        Save
                                    </Button>
                                </div>
                            </div>
                        </div>
                        <Button
                            className="mt-4"
                            variant="secondary"
                            onClick={() => setFooterGroupModalOpen(true)}
                        >
                            Open modal with this footer
                        </Button>
                    </div>
                </section>

                {/* ========================================
                      LINKS SECTION
                      ======================================== */}
                <section className="mb-12">
                    <Heading2 className="mb-6">Links</Heading2>
                    <div className="bg-white p-6 rounded-lg shadow-md border">
                        <Heading4 className="mb-4">Link Component</Heading4>
                        <div className="space-y-4">
                            <Link to="/example">Basic Link Example</Link>
                        </div>
                    </div>
                </section>

                {/* ========================================
                     SPINNER SECTION
                     ======================================== */}
                <section className="mb-12">
                    <Heading2 className="mb-6">Spinners</Heading2>
                    <div className="bg-white p-6 rounded-lg shadow-md border">
                        <Heading4 className="mb-4">Size Variants</Heading4>
                        <SmallText className="mb-4 block">Large and extra-large spinners use the June logo mark.</SmallText>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                            <div className="text-center">
                                <SmallText className="mb-2">Small</SmallText>
                                <Spinner size="sm" />
                            </div>
                            <div className="text-center">
                                <SmallText className="mb-2">Medium</SmallText>
                                <Spinner size="md" />
                            </div>
                            <div className="text-center">
                                <SmallText className="mb-2">Large</SmallText>
                                <Spinner size="lg" />
                            </div>
                            <div className="text-center">
                                <SmallText className="mb-2">Extra Large</SmallText>
                                <Spinner size="xl" />
                            </div>
                        </div>
                    </div>
                </section>

                {/* ========================================
                     BADGE SECTION
                     ======================================== */}
                <section className="mb-12">
                    <Heading2 className="mb-6">Badges</Heading2>
                    <div className="bg-white p-6 rounded-lg shadow-md border">
                        <Heading4 className="mb-4">Badge Component</Heading4>
                        <div className="space-y-4">
                            <div>
                                <Heading5 className="mb-2">Status-based Badges (Smart Color Mapping)</Heading5>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                                    <div className="flex items-center justify-center">
                                        <Badge status="not_sent">Not Sent</Badge>
                                    </div>
                                    <div className="flex items-center justify-center">
                                        <Badge status="pending">Pending</Badge>
                                    </div>
                                    <div className="flex items-center justify-center">
                                        <Badge status="in_progress">In Progress</Badge>
                                    </div>
                                    <div className="flex items-center justify-center">
                                        <Badge status="completed">Completed</Badge>
                                    </div>
                                    <div className="flex items-center justify-center">
                                        <Badge status="stopped_by_applicant">Stopped by Applicant</Badge>
                                    </div>
                                    <div className="flex items-center justify-center">
                                        <Badge status="abandoned">Abandoned</Badge>
                                    </div>
                                </div>
                                <p className="text-small text-gray-600 mt-2">
                                    The Badge component automatically determines colors based on status strings.
                                    Pass <code className="bg-gray-100 px-1 rounded">status="completed"</code> instead of manually mapping to colors.
                                </p>
                            </div>

                            <div>
                                <Heading5 className="mb-2">Manual Variant Badges</Heading5>
                                <div className="flex space-x-2">
                                    <Badge variant="success">Success</Badge>
                                    <Badge variant="warning">Warning</Badge>
                                    <Badge variant="failure">Failure</Badge>
                                    <Badge variant="purple">Purple</Badge>
                                    <Badge variant="gray">Gray</Badge>
                                    <Badge variant="default">Default</Badge>
                                </div>
                                <p className="text-small text-gray-600 mt-2">
                                    You can still use manual variants when needed: <code className="bg-gray-100 px-1 rounded">variant="success"</code>
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ========================================
                      DROPDOWN SECTION
                      ======================================== */}
                <section className="mb-12">
                    <Heading2 className="mb-6">Dropdowns</Heading2>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="bg-white p-6 rounded-lg shadow-md border">
                            <Heading4 className="mb-4">Actions Menu</Heading4>
                            <div className="flex items-center space-x-4">
                                <SmallText>Click the 3-dot menu:</SmallText>
                                <Dropdown
                                    id="actions-demo"
                                    items={[
                                        {
                                            label: 'Edit',
                                            onClick: () => toast.success('Edit clicked!'),
                                            variant: 'normal'
                                        },
                                        {
                                            label: 'Duplicate',
                                            onClick: () => toast.success('Duplicate clicked!'),
                                            variant: 'normal'
                                        },
                                        {
                                            label: 'Delete',
                                            onClick: () => toast.success('Delete clicked!'),
                                            variant: 'danger'
                                        }
                                    ]}
                                />
                            </div>
                        </div>

                        <div className="bg-white p-6 rounded-lg shadow-md border">
                            <Heading4 className="mb-4">Disabled Menu</Heading4>
                            <div className="flex items-center space-x-4">
                                <SmallText>Disabled state:</SmallText>
                                <Dropdown
                                    id="disabled-demo"
                                    disabled={true}
                                    items={[
                                        {
                                            label: 'Action 1',
                                            onClick: () => { },
                                            color: ''
                                        }
                                    ]}
                                />
                            </div>
                        </div>
                    </div>
                </section>

                {/* ========================================
                      BREADCRUMB SECTION
                      ======================================== */}
                <section className="mb-12">
                    <Heading2 className="mb-6">Breadcrumbs</Heading2>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="bg-white p-6 rounded-lg shadow-md border">
                            <Heading4 className="mb-4">Single Item (Page Title)</Heading4>
                            <div className="space-y-4">
                                <div>
                                    <SmallText className="text-gray-600 mb-2">Role Page:</SmallText>
                                    <Breadcrumb
                                        items={[
                                            {
                                                label: 'Roles',
                                                href: '/jobs',
                                                onClick: (e) => {
                                                    e.preventDefault();
                                                    toast.success('Navigating to Jobs page!');
                                                },
                                                isActive: true
                                            }
                                        ]}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="bg-white p-6 rounded-lg shadow-md border">
                            <Heading4 className="mb-4">Multiple Items (Navigation)</Heading4>
                            <div className="space-y-4">
                                <div>
                                    <SmallText className="text-gray-600 mb-2">With Navigation:</SmallText>
                                    <Breadcrumb
                                        items={[
                                            {
                                                label: 'Jobs',
                                                href: '/jobs',
                                                onClick: (e) => {
                                                    e.preventDefault();
                                                    toast.success('Navigating to Jobs page!');
                                                },
                                                isActive: false
                                            },
                                            {
                                                label: 'Senior Frontend Developer',
                                                href: '#',
                                                onClick: null,
                                                isActive: true
                                            }
                                        ]}
                                    />
                                </div>
                                <div>
                                    <SmallText className="text-gray-600 mb-2">Three Levels:</SmallText>
                                    <Breadcrumb
                                        items={[
                                            {
                                                label: 'Jobs',
                                                href: '/jobs',
                                                onClick: (e) => {
                                                    e.preventDefault();
                                                    toast.success('Navigating to Jobs page!');
                                                },
                                                isActive: false
                                            },
                                            {
                                                label: 'Engineering',
                                                href: '/jobs/engineering',
                                                onClick: (e) => {
                                                    e.preventDefault();
                                                    toast.success('Navigating to Engineering jobs!');
                                                },
                                                isActive: false
                                            },
                                            {
                                                label: 'Senior Frontend Developer',
                                                href: '#',
                                                onClick: null,
                                                isActive: true
                                            }
                                        ]}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ========================================
                       MATCH RATING SECTION
                       ======================================== */}
                <section className="mb-12">
                    <Heading2 className="mb-6">Match Rating</Heading2>
                    <div className="bg-white p-6 rounded-lg shadow-md border">
                        <Heading4 className="mb-4">Rating Examples</Heading4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                            <div className="text-center">
                                <MatchRating rating={0} />
                            </div>
                            <div className="text-center">
                                <MatchRating rating={1} />
                            </div>
                            <div className="text-center">
                                <MatchRating rating={2} />
                            </div>
                            <div className="text-center">
                                <MatchRating rating={3} />
                            </div>
                            <div className="text-center">
                                <MatchRating rating={4} />
                            </div>
                            <div className="text-center">
                                <MatchRating rating={5} />
                            </div>
                        </div>
                    </div>
                </section>

                {/* ========================================
                      INPUT FIELDS SECTION
                      ======================================== */}
                <section className="mb-12">
                    <Heading2 className="mb-6">Input Fields</Heading2>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="bg-white p-6 rounded-lg shadow-md border">
                            <Heading4 className="mb-4">Text Input</Heading4>
                            <div className="space-y-4">
                                <Input placeholder="Default input" />
                                <Input placeholder="Error state" error />
                                <Input placeholder="Disabled input" disabled />
                                <Input value="Read-only value" readOnly className="cursor-default" />
                                <Select defaultValue="weekly">
                                    <option value="daily">Daily</option>
                                    <option value="weekly">Weekly</option>
                                </Select>
                            </div>
                        </div>
                        <div className="bg-white p-6 rounded-lg shadow-md border">
                            <Heading4 className="mb-4">Textarea</Heading4>
                            <div className="space-y-4">
                                <Textarea rows={3} placeholder="Default textarea" />
                                <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                                    <Textarea rows={3} background="white" placeholder='White background variant (background="white") for tinted surfaces' />
                                </div>
                            </div>
                        </div>
                        <div className="bg-white p-6 rounded-lg shadow-md border lg:col-span-2">
                            <Heading4 className="mb-4">Search Input</Heading4>
                            <BodyText className="mb-3">
                                SearchInput — shared list-page search field with leading icon and clear control. Used on Roles and Applicants.
                            </BodyText>
                            <SearchInput
                                value={showcaseSearch}
                                onChange={(e) => setShowcaseSearch(e.target.value)}
                                onClear={() => setShowcaseSearch('')}
                                placeholder="Search"
                            />
                        </div>
                    </div>
                </section>

                {/* ========================================
                      TOGGLE FIELD SECTION
                      ======================================== */}
                <section className="mb-12">
                    <Heading2 className="mb-6">Toggle field</Heading2>
                    <p className="mb-4 text-body text-gray-600">
                        Use <code className="text-body">ToggleField</code> whenever a switch has a title and optional helper text — toggle on the left, labels on the right.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-white p-6 rounded-lg shadow-md border">
                            <Heading4 className="mb-4">Default</Heading4>
                            <ToggleField
                                label="Receive digests"
                                description="When on, you get scheduled shortlist emails for the roles you enable below."
                                checked={toggleFieldOn}
                                onChange={(v) => {
                                    setToggleFieldOn(v);
                                    toast.success(`Digests ${v ? 'on' : 'off'}`);
                                }}
                            />
                        </div>
                        <div className="bg-white p-6 rounded-lg shadow-md border">
                            <Heading4 className="mb-4">Disabled with tooltip</Heading4>
                            <ToggleField
                                label="Receive digests"
                                description="When on, you get scheduled shortlist emails for the roles you enable below."
                                checked={false}
                                disabled
                                disabledTooltip="Contact your company admin if you want shortlist digests turned on."
                            />
                        </div>
                    </div>
                </section>

                {/* ========================================
                      CHECKBOX SECTION
                      ======================================== */}
                <section className="mb-12">
                    <Heading2 className="mb-6">Checkboxes</Heading2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
                        <div className="bg-white p-6 rounded-lg shadow-md border">
                            <Heading4 className="mb-4">Checkbox with Label</Heading4>
                            <div className="flex items-center gap-2">
                                <Checkbox
                                    checked={checkboxValue}
                                    onChange={(e) => {
                                        setCheckboxValue(e.target.checked);
                                        toast.success(`Checkbox ${e.target.checked ? 'checked' : 'unchecked'}!`);
                                    }}
                                />
                                <SmallText className="cursor-pointer" onClick={() => {
                                    const checkbox = document.querySelector('input[type="checkbox"]');
                                    if (checkbox) {
                                        checkbox.checked = !checkbox.checked;
                                        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
                                    }
                                }}>
                                    Sample Checkbox
                                </SmallText>
                            </div>
                            <SmallText className="mt-3">Value: {checkboxValue.toString()}</SmallText>
                        </div>

                        <div className="bg-white p-6 rounded-lg shadow-md border">
                            <Heading4 className="mb-4">Checkbox without Label</Heading4>
                            <Checkbox
                                checked={checkboxValue2}
                                onChange={(e) => {
                                    setCheckboxValue2(e.target.checked);
                                    toast.success(`Checkbox ${e.target.checked ? 'checked' : 'unchecked'}!`);
                                }}
                            />
                            <SmallText className="mt-3">Value: {checkboxValue2.toString()}</SmallText>
                        </div>
                    </div>
                </section>

                {/* ========================================
                     TABLE SECTION
                     ======================================== */}
                <section className="mb-12">
                    <Heading2 className="mb-6">Tables</Heading2>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="bg-white p-6 rounded-lg shadow-md border">
                            <Heading4 className="mb-4">Basic Table</Heading4>
                            <Table>
                                <Table.Head>
                                    <Table.HeadCell>Name</Table.HeadCell>
                                    <Table.HeadCell>Role</Table.HeadCell>
                                    <Table.HeadCell>Status</Table.HeadCell>
                                </Table.Head>
                                <Table.Body>
                                    <Table.Row>
                                        <Table.Cell>John Doe</Table.Cell>
                                        <Table.Cell>Frontend Developer</Table.Cell>
                                        <Table.Cell>
                                            <Badge status="active">Active</Badge>
                                        </Table.Cell>
                                    </Table.Row>
                                    <Table.Row>
                                        <Table.Cell>Jane Smith</Table.Cell>
                                        <Table.Cell>Backend Developer</Table.Cell>
                                        <Table.Cell>
                                            <Badge status="pending">Pending</Badge>
                                        </Table.Cell>
                                    </Table.Row>
                                    <Table.Row>
                                        <Table.Cell>Bob Johnson</Table.Cell>
                                        <Table.Cell>Designer</Table.Cell>
                                        <Table.Cell>
                                            <Badge status="inactive">Inactive</Badge>
                                        </Table.Cell>
                                    </Table.Row>
                                </Table.Body>
                            </Table>
                        </div>

                        <div className="bg-white p-6 rounded-lg shadow-md border">
                            <Heading4 className="mb-4">Hoverable Table</Heading4>
                            <Table hoverable>
                                <Table.Head>
                                    <Table.HeadCell>Name</Table.HeadCell>
                                    <Table.HeadCell>Role</Table.HeadCell>
                                    <Table.HeadCell>Status</Table.HeadCell>
                                </Table.Head>
                                <Table.Body>
                                    <Table.Row>
                                        <Table.Cell>Alice Cooper</Table.Cell>
                                        <Table.Cell>Product Manager</Table.Cell>
                                        <Table.Cell>
                                            <Badge status="active">Active</Badge>
                                        </Table.Cell>
                                    </Table.Row>
                                    <Table.Row>
                                        <Table.Cell>Charlie Brown</Table.Cell>
                                        <Table.Cell>UX Designer</Table.Cell>
                                        <Table.Cell>
                                            <Badge status="pending">Pending</Badge>
                                        </Table.Cell>
                                    </Table.Row>
                                    <Table.Row>
                                        <Table.Cell>Diana Prince</Table.Cell>
                                        <Table.Cell>DevOps Engineer</Table.Cell>
                                        <Table.Cell>
                                            <Badge status="inactive">Inactive</Badge>
                                        </Table.Cell>
                                    </Table.Row>
                                </Table.Body>
                            </Table>
                        </div>

                        <div className="bg-white p-6 rounded-lg shadow-md border">
                            <Heading4 className="mb-4">Striped Table</Heading4>
                            <Table striped>
                                <Table.Head>
                                    <Table.HeadCell>Name</Table.HeadCell>
                                    <Table.HeadCell>Role</Table.HeadCell>
                                    <Table.HeadCell>Actions</Table.HeadCell>
                                </Table.Head>
                                <Table.Body>
                                    <Table.Row>
                                        <Table.Cell>Alice Cooper</Table.Cell>
                                        <Table.Cell>Product Manager</Table.Cell>
                                        <Table.Cell>
                                            <div className="flex space-x-2">
                                                <Button size="xs" variant="secondary">Edit</Button>
                                                <Button size="xs" variant="tertiary">View</Button>
                                            </div>
                                        </Table.Cell>
                                    </Table.Row>
                                    <Table.Row>
                                        <Table.Cell>Charlie Brown</Table.Cell>
                                        <Table.Cell>UX Designer</Table.Cell>
                                        <Table.Cell>
                                            <div className="flex space-x-2">
                                                <Button size="xs" variant="secondary">Edit</Button>
                                                <Button size="xs" variant="tertiary">View</Button>
                                            </div>
                                        </Table.Cell>
                                    </Table.Row>
                                    <Table.Row>
                                        <Table.Cell>Diana Prince</Table.Cell>
                                        <Table.Cell>DevOps Engineer</Table.Cell>
                                        <Table.Cell>
                                            <div className="flex space-x-2">
                                                <Button size="xs" variant="secondary">Edit</Button>
                                                <Button size="xs" variant="tertiary">View</Button>
                                            </div>
                                        </Table.Cell>
                                    </Table.Row>
                                </Table.Body>
                            </Table>
                        </div>

                        <div className="bg-white p-6 rounded-lg shadow-md border">
                            <Heading4 className="mb-4">Table with Checkboxes</Heading4>
                            <Table>
                                <Table.Head>
                                    <Table.HeadCell></Table.HeadCell>
                                    <Table.HeadCell>Name</Table.HeadCell>
                                    <Table.HeadCell>Role</Table.HeadCell>
                                    <Table.HeadCell>Status</Table.HeadCell>
                                </Table.Head>
                                <Table.Body>
                                    <Table.Row>
                                        <Table.Cell>
                                            <Checkbox
                                                checked={checkboxValue}
                                                onChange={(e) => {
                                                    setCheckboxValue(e.target.checked);
                                                    toast.success(`Row 1 ${e.target.checked ? 'checked' : 'unchecked'}!`);
                                                }}
                                            />
                                        </Table.Cell>
                                        <Table.Cell>John Doe</Table.Cell>
                                        <Table.Cell>Frontend Developer</Table.Cell>
                                        <Table.Cell>
                                            <Badge status="active">Active</Badge>
                                        </Table.Cell>
                                    </Table.Row>
                                    <Table.Row>
                                        <Table.Cell>
                                            <Checkbox
                                                checked={checkboxValue2}
                                                onChange={(e) => {
                                                    setCheckboxValue2(e.target.checked);
                                                    toast.success(`Row 2 ${e.target.checked ? 'checked' : 'unchecked'}!`);
                                                }}
                                            />
                                        </Table.Cell>
                                        <Table.Cell>Jane Smith</Table.Cell>
                                        <Table.Cell>Backend Developer</Table.Cell>
                                        <Table.Cell>
                                            <Badge status="pending">Pending</Badge>
                                        </Table.Cell>
                                    </Table.Row>
                                </Table.Body>
                            </Table>
                        </div>

                        <div className="bg-white p-6 rounded-lg shadow-md border">
                            <Heading4 className="mb-4">Sortable Table</Heading4>
                            <Table>
                                <Table.Head>
                                    <Table.HeadCell
                                        sortable
                                        onSort={() => handleSort('name')}
                                        sortDirection={sortConfig.key === 'name' ? sortConfig.direction : null}
                                    >
                                        Name
                                    </Table.HeadCell>
                                    <Table.HeadCell
                                        sortable
                                        onSort={() => handleSort('role')}
                                        sortDirection={sortConfig.key === 'role' ? sortConfig.direction : null}
                                    >
                                        Role
                                    </Table.HeadCell>
                                    <Table.HeadCell>
                                        Status
                                    </Table.HeadCell>
                                </Table.Head>
                                <Table.Body>
                                    {sortableData.map((row, index) => (
                                        <Table.Row key={index}>
                                            <Table.Cell>{row.name}</Table.Cell>
                                            <Table.Cell>{row.role}</Table.Cell>
                                            <Table.Cell>
                                                <Badge
                                                    status={
                                                        row.status === 'Active' ? 'active' :
                                                            row.status === 'Pending' ? 'pending' : 'inactive'
                                                    }
                                                >
                                                    {row.status}
                                                </Badge>
                                            </Table.Cell>
                                        </Table.Row>
                                    ))}
                                </Table.Body>
                            </Table>
                        </div>

                        <div className="bg-white p-6 rounded-lg shadow-md border">
                            <Heading4 className="mb-4">Loading Table</Heading4>
                            <Table>
                                <Table.Head>
                                    <Table.HeadCell>Name</Table.HeadCell>
                                    <Table.HeadCell>Role</Table.HeadCell>
                                    <Table.HeadCell>Status</Table.HeadCell>
                                </Table.Head>
                                <Table.Body loading>
                                </Table.Body>
                            </Table>
                        </div>

                        <div className="bg-white p-6 rounded-lg shadow-md border col-span-2">
                            <Heading4 className="mb-4">Paginated Table</Heading4>
                            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <p className="text-small text-blue-800">
                                    <strong>Pagination Demo:</strong> This table has 29 items. With 3 items per page, you'll see 10 pages (last page has only 2 items).
                                    The pagination shows ellipsis (...) when there are many pages, keeping the interface clean.
                                </p>
                            </div>
                            <div className="flex justify-between items-center mb-4">
                                <span className="text-small text-gray-600">
                                    Showing {startIndex + 1} to {Math.min(endIndex, paginatedData.length)} of {paginatedData.length} results
                                </span>
                                <div className="flex items-center gap-2">
                                    <span className="text-small text-gray-600">Show:</span>
                                    <select
                                        value={itemsPerPage}
                                        onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
                                        className="border border-gray-300 rounded-md px-2 py-1 text-small focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    >
                                        <option value={2}>2</option>
                                        <option value={3}>3</option>
                                        <option value={5}>5</option>
                                        <option value={10}>10</option>
                                    </select>
                                    <span className="text-small text-gray-600">per page</span>
                                </div>
                            </div>
                            <Table
                                paginated={true}
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                                itemsPerPage={itemsPerPage}
                                onItemsPerPageChange={handleItemsPerPageChange}
                            >
                                <Table.Head>
                                    <Table.HeadCell>ID</Table.HeadCell>
                                    <Table.HeadCell>Name</Table.HeadCell>
                                    <Table.HeadCell>Role</Table.HeadCell>
                                    <Table.HeadCell>Department</Table.HeadCell>
                                    <Table.HeadCell>Status</Table.HeadCell>
                                </Table.Head>
                                <Table.Body>
                                    {currentData.map((row, index) => (
                                        <Table.Row key={row.id}>
                                            <Table.Cell>{row.id}</Table.Cell>
                                            <Table.Cell>{row.name}</Table.Cell>
                                            <Table.Cell>{row.role}</Table.Cell>
                                            <Table.Cell>{row.department}</Table.Cell>
                                            <Table.Cell>
                                                <Badge
                                                    variant={
                                                        row.status === 'Active' ? 'success' :
                                                            row.status === 'Pending' ? 'warning' : 'failure'
                                                    }
                                                >
                                                    {row.status}
                                                </Badge>
                                            </Table.Cell>
                                        </Table.Row>
                                    ))}
                                </Table.Body>
                            </Table>
                        </div>


                    </div>
                </section>

                {/* ========================================
                     MODAL SECTION
                     ======================================== */}
                <section className="mb-12">
                    <Heading2 className="mb-6">Modals</Heading2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-white p-6 rounded-lg shadow-md border">
                            <Heading4 className="mb-4">Simple modal</Heading4>
                            <Button onClick={() => setModalOpen(true)}>Open Modal</Button>
                            <Modal show={modalOpen} onClose={() => setModalOpen(false)} size="md" placement="center">
                                <Modal.Header className="!border-b border-gray-200">
                                    <Heading4 className="!mb-0">Sample Modal</Heading4>
                                </Modal.Header>
                                <Modal.Body className="pt-4">
                                    <NormalParagraph>
                                        Body content uses the shared body size and spacing.
                                    </NormalParagraph>
                                </Modal.Body>
                                <Modal.Footer className="!border-t border-gray-200 flex flex-wrap items-center justify-between gap-3">
                                    <Button variant="ghost" onClick={() => setModalOpen(false)}>
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="primary"
                                        className="ml-auto"
                                        onClick={() => {
                                            setModalOpen(false);
                                            toast.success('Confirmed');
                                        }}
                                    >
                                        Confirm
                                    </Button>
                                </Modal.Footer>
                            </Modal>
                        </div>
                        <div className="bg-white p-6 rounded-lg shadow-md border">
                            <Heading4 className="mb-4">Footer button group</Heading4>
                            <NormalParagraph className="mb-4 text-gray-600">
                                Same pattern as the Buttons section — Cancel ghost left; Reset + Save on the right.
                            </NormalParagraph>
                            <Button variant="secondary" onClick={() => setFooterGroupModalOpen(true)}>
                                Open example modal
                            </Button>
                            <Modal
                                show={footerGroupModalOpen}
                                onClose={() => setFooterGroupModalOpen(false)}
                                size="md"
                                placement="center"
                            >
                                <Modal.Header className="!border-b border-gray-200">
                                    <Heading4 className="!mb-0">Schedule for this role</Heading4>
                                </Modal.Header>
                                <Modal.Body className="pt-4">
                                    <NormalParagraph>
                                        Form fields go in the body; actions belong in the footer row below.
                                    </NormalParagraph>
                                </Modal.Body>
                                <Modal.Footer className="!border-t border-gray-200 flex flex-wrap items-center justify-between gap-3">
                                    <Button variant="ghost" onClick={() => setFooterGroupModalOpen(false)}>
                                        Cancel
                                    </Button>
                                    <div className="flex flex-wrap items-center justify-end gap-3 ml-auto">
                                        <Button
                                            variant="secondary"
                                            onClick={() => toast.info('Reset to Default')}
                                        >
                                            Reset to Default
                                        </Button>
                                        <Button
                                            variant="primary"
                                            onClick={() => {
                                                setFooterGroupModalOpen(false);
                                                toast.success('Saved');
                                            }}
                                        >
                                            Save
                                        </Button>
                                    </div>
                                </Modal.Footer>
                            </Modal>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default ComponentShowcase;
